/**
 * V2StoryboardCard — the storyboard offered for MANUAL approval before a video
 * is produced. The director writes it (description + scene list + the length it
 * planned); approving starts the real production and charges Vivideo credits,
 * declining cancels the video. The card is a settled record once it is resolved
 * or its 24h window expires, so it can never fire twice.
 *
 * It wears V2PlanCard's chrome — collapsible header, gradient title banner,
 * detail chips, scene table, full-width approve footer — so the two approval
 * gates read as the same product. It stays a SIBLING of that card rather than a
 * variant of it: none of the HeyGen avatar/voice/template resolution or scene
 * editing there applies here, and V2PlanCard renders for every paid agent
 * project, which is not a file to add conditionals to.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Check, X, Sparkles, Clock, Film, ChevronDown, RectangleHorizontal } from 'lucide-react';
import { cn } from '@/v2/utils';
import { MarkdownText } from '@/v2/components/MarkdownText';
import { useUserStore } from '@/store/userStore';
import { calculateHeygenCredits } from '@/utils/constants';
import type { V2AgentStoryboard, V2LongvidTier } from '@/api/v2Service';

/** mm:ss (or h:mm:ss) left until `expiresAt` (unix seconds); '' once it has passed. */
function formatLeft(seconds: number): string {
  if (seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export interface V2StoryboardCardProps {
  storyboard: V2AgentStoryboard;
  /** Live = show Approve/Decline; otherwise a settled record. */
  actionable: boolean;
  busy?: boolean;
  /** 'superseded': the director replaced this plan — a locked past card with its own verdict. */
  resolved?: 'approved' | 'declined' | 'expired' | 'superseded' | null;
  /** The price in VIVIDEO credits, as quoted by the backend on this turn.
   *  Falls back to the planned length × the standard credits rule. */
  credits?: number;
  /** Kept for the settled record of older approvals; no tier is picked here. */
  tier?: V2LongvidTier | null;
  onApprove: () => void;
  onDecline: () => void;
}

export function V2StoryboardCard({ storyboard, actionable, busy, resolved, credits, onApprove, onDecline }: V2StoryboardCardProps) {
  const { t } = useTranslation('v2');
  const isPro = useUserStore((s) => s.isPro);
  const balance = useUserStore((s) => s.user?.remainingCredit ?? 0);
  const [collapsed, setCollapsed] = useState(false);

  // DynamoDB Decimals arrive as STRINGS once the order has been re-read, so every
  // number on this card is coerced before it is rendered or compared.
  const scenes = storyboard.scenes || [];
  const plannedSec = Math.round(
    Number(storyboard.planned_sec ?? storyboard.duration_sec)
    || scenes.reduce((sum, s) => sum + (Number(s.duration_sec) || 0), 0),
  );
  // The server prices the card; the seconds rule is the fallback when it did not.
  const price = Math.round(Number(credits) || 0) || calculateHeygenCredits(plannedSec);
  const portrait = String(storyboard.aspect_ratio || '') === '9:16';

  const expiresAt = Number(storyboard.expires_at) || 0;
  const [left, setLeft] = useState(() => expiresAt - Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setLeft(expiresAt - Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const expired = resolved === 'expired' || (!!expiresAt && left <= 0);
  const live = actionable && !resolved && !expired;

  return (
    <div className={cn(
      'rounded-2xl overflow-hidden border backdrop-blur-sm shadow-glow-sm animate-slide-up transition-colors duration-300',
      collapsed ? 'border-primary/40 bg-primary/10' : 'border-glass-border bg-surface/80',
    )}>
      {/* Top bar — click anywhere to toggle. Collapsed = a thicker purple banner. */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        className={cn(
          'w-full flex items-center gap-3 text-left transition-all duration-300',
          collapsed ? 'px-5 py-4 hover:bg-primary/15' : 'px-4 py-3 border-b border-glass-border hover:bg-surface-light/30',
        )}
      >
        <span className={cn(
          'flex items-center justify-center w-7 h-7 rounded-lg shrink-0 transition-colors',
          collapsed ? 'bg-primary/20 text-primary' : 'text-text-secondary',
        )}>
          <ChevronDown className={cn('w-4 h-4 transition-transform duration-300', collapsed && '-rotate-90')} />
        </span>
        {collapsed ? (
          <>
            <Film className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm font-bold text-text-primary truncate">{t('v2StoryboardCard.title')}</span>
            <div className="flex-1" />
            {/* Price shown to PRO only — free users never see one, so this reads as free. */}
            {live && isPro() && price > 0 && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/20 border border-primary/40 text-xs font-bold text-primary shrink-0">
                <Sparkles className="w-3.5 h-3.5" /> {t('v2PlanCard.creditsCount', { count: price })}
              </span>
            )}
          </>
        ) : (
          <span className="text-sm font-semibold text-text-secondary">{t('v2StoryboardCard.collapse')}</span>
        )}
      </button>

      {/* Body — smooth height animation via grid-rows (0fr ⇄ 1fr) */}
      <div className={cn('grid transition-[grid-template-rows] duration-300 ease-out', collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]')}>
        <div className="min-h-0 overflow-hidden">
          {/* Title banner (gradient only — no cover image) */}
          <div className="relative h-24 sm:h-28 bg-gradient-to-r from-primary/30 via-accent/25 to-primary/20 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <h3 className="absolute bottom-3 left-5 right-5 text-xl sm:text-2xl font-extrabold text-white leading-tight drop-shadow">
              {t('v2StoryboardCard.title')}
            </h3>
          </div>

          <div className="p-4 sm:p-5 space-y-5">
            {storyboard.description && (
              <div>
                <p className="text-sm font-semibold text-text-primary mb-1.5">{t('v2StoryboardCard.summary')}</p>
                <p className="text-sm text-text-secondary leading-relaxed"><MarkdownText text={storyboard.description} /></p>
              </div>
            )}

            {/* Details chips */}
            <div>
              <p className="text-sm font-semibold text-text-primary mb-2">{t('v2PlanCard.details')}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-text-primary">
                {plannedSec > 0 && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-glass-border bg-surface-light/40">
                    <Clock className="w-3.5 h-3.5 text-text-secondary" /> {t('v2PlanCard.durationSeconds', { seconds: plannedSec })}
                  </span>
                )}
                {!!storyboard.aspect_ratio && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-glass-border bg-surface-light/40">
                    <RectangleHorizontal className={cn('w-3.5 h-3.5 text-text-secondary', portrait && 'rotate-90')} />
                    {portrait ? t('agentProject.orientationPortrait') : t('agentProject.orientationLandscape')}
                  </span>
                )}
                {isPro() && price > 0 && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary font-semibold">
                    <Sparkles className="w-3.5 h-3.5" /> {t('v2PlanCard.creditsCount', { count: price })}
                  </span>
                )}
              </div>
              {live && (
                <p className="text-[11px] text-text-tertiary mt-2">{t('v2PlanCard.lengthHint')}</p>
              )}
            </div>

            {/* Scene breakdown — Scene | What happens, stacked on a phone. */}
            {scenes.length > 0 && (
              <div>
                <div className="hidden sm:grid grid-cols-[9rem_1fr] gap-x-4 px-3 pb-2 text-xs font-medium text-text-tertiary">
                  <span>{t('v2PlanCard.scene')}</span><span>{t('v2StoryboardCard.whatHappens')}</span>
                </div>
                <div className="rounded-xl overflow-hidden border border-glass-border divide-y divide-glass-border">
                  {scenes.map((s, i) => (
                    <div
                      key={s.n ?? i}
                      className={cn(
                        'p-3 flex flex-col gap-2.5 sm:grid sm:grid-cols-[9rem_1fr] sm:gap-x-4 sm:gap-y-0',
                        i % 2 === 0 ? 'bg-surface-light/40' : 'bg-transparent',
                      )}
                    >
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 sm:block min-w-0">
                        <p className="text-sm font-semibold text-text-primary leading-snug">{t('v2PlanCard.sceneNumber', { number: s.n ?? i + 1 })}</p>
                        {Number(s.duration_sec) > 0 && (
                          <p className="text-[11px] text-text-tertiary tabular-nums sm:mt-0.5">
                            {t('v2PlanCard.durationSeconds', { seconds: Math.round(Number(s.duration_sec)) })}
                          </p>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-text-secondary leading-relaxed">{s.plot}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {live && !!expiresAt && (
              <p className="flex items-center gap-1.5 text-[11px] text-text-tertiary tabular-nums">
                <Clock className="w-3.5 h-3.5" /> {t('v2StoryboardCard.expiresIn', { time: formatLeft(left) })}
              </p>
            )}

            {live && (
              <div className="pt-1 space-y-2">
                {isPro() && price > 0 && balance < price && (
                  <p className="text-[11px] text-amber-300/90">
                    {t('v2PlanCard.insufficientBalancePrefix')} <span className="font-semibold">{t('v2PlanCard.creditsCount', { count: price })}</span> {t('v2PlanCard.insufficientBalanceSuffix', { balance })}
                  </p>
                )}
                <button
                  onClick={() => onApprove()}
                  disabled={busy}
                  className="relative w-full py-4 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-base font-bold hover:opacity-90 hover:shadow-glow transition-all flex items-center justify-center gap-2.5 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                  <span>{t('v2StoryboardCard.approve')}</span>
                  {isPro() && price > 0 && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1 rounded-lg bg-white/25 text-white text-sm font-extrabold shadow-sm">
                      <Sparkles className="w-4 h-4" /> {t('v2PlanCard.creditsCount', { count: price })}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => onDecline()}
                  disabled={busy}
                  className="w-full py-2 text-sm font-semibold text-text-tertiary hover:text-text-primary underline underline-offset-2 transition-colors disabled:opacity-60"
                >
                  {t('v2StoryboardCard.decline')}
                </button>
              </div>
            )}

            {resolved === 'approved' && (
              <p className="text-xs font-bold flex items-center gap-1.5 text-emerald-400">
                <Check className="w-3.5 h-3.5" /> {t('v2StoryboardCard.approvedProducing')}
              </p>
            )}
            {resolved === 'declined' && (
              <p className="text-xs font-bold flex items-center gap-1.5 text-text-tertiary">
                <X className="w-3.5 h-3.5" /> {t('v2StoryboardCard.declined')}
              </p>
            )}
            {resolved === 'superseded' && (
              /* Replaced by a newer plan, after a revision the user asked for in chat. */
              <p className="text-xs font-bold flex items-center gap-1.5 text-text-tertiary">
                <Clock className="w-3.5 h-3.5" /> {t('agentProject.longvidCardSuperseded')}
              </p>
            )}
            {!resolved && expired && (
              <p className="text-xs font-bold flex items-center gap-1.5 text-text-tertiary">
                <Clock className="w-3.5 h-3.5" /> {t('agentProject.longvidCardExpired')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
